import { SelectChangeEvent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import { default as MatSelect } from '@mui/material/Select';
import React, { useCallback } from 'react';

export interface SelectOption<T> {
  value: T;
  label: string | number;
}

export interface SelectProps<T> {
  className?: string;
  value?: SelectOption<T> | null;
  defaultValue?: SelectOption<T> | null;
  name?: string;
  title: string;
  options: SelectOption<T>[];
  onChange: (value: SelectOption<T>) => void;
  isDisabled?: boolean;
  placeholder?: string;
  fullWidth?: boolean;
  variant?: 'outlined' | 'standard';
  size?: 'small' | 'medium';
}

const Select = <T,>({
  value,
  name,
  options,
  onChange,
  title,
  isDisabled = false,
  className,
  placeholder,
  defaultValue,
  fullWidth,
  variant = 'standard',
  size,
}: SelectProps<T>) => {
  const handleOnChange = useCallback(
    (event: SelectChangeEvent<T>) => {
      const selectedValue = event.target.value;
      const selectedOption = options.find(
        option => option.value === selectedValue,
      );
      if (selectedOption) onChange(selectedOption);
    },
    [onChange, options],
  );

  return (
    <FormControl
      size={size}
      placeholder={placeholder}
      className={className}
      name={name}
      disabled={isDisabled}
      variant={variant}
      fullWidth={fullWidth}
    >
      <InputLabel>{title}</InputLabel>
      <MatSelect
        slotProps={{}}
        defaultValue={defaultValue?.value}
        value={value?.value ?? ''}
        name={name}
        disabled={isDisabled}
        onChange={handleOnChange}
        label={title}
        MenuProps={{ disableScrollLock: true }}
      >
        {options.map((option, index) => (
          <MenuItem key={index} value={option.value as string | number}>
            {option.label}
          </MenuItem>
        ))}
      </MatSelect>
    </FormControl>
  );
};

export default Select;
